import React from 'react';
import { ConsumptionTax } from '@/contexts/dynamic-config';
import {
  CreditRequestBatchListOption,
  ErpOrderListOption,
  OrderHistoryListOption,
  PepperOrderListOption,
} from '@/utils/order-helpers';
import { CreditRequestListItem } from './credit-request-list-item';
import { ExternalOrderHistoryListItem } from './external-order-history-list-item';
import {
  LIST_ITEM_HEIGHT,
  LIST_ITEM_MARGIN,
} from '@/components/history-list-item';
import { ListOption } from '@/components/list-item';
import { OrderHistoryListItem } from './order-history-list-item';
import {
  RecyclerList,
  Props as RecyclerListProps,
} from '@/components/recycler-list';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

// Type guard functions to determine the type ListOption
const listOptionIsPepperOrder = (
  item: OrderHistoryListOption,
): item is ListOption<PepperOrderListOption> => {
  return Boolean(item.metadata?.hasOwnProperty('order'));
};
const listOptionIsErpOrder = (
  item: OrderHistoryListOption,
): item is ListOption<ErpOrderListOption> => {
  return Boolean(item.metadata?.hasOwnProperty('invoice'));
};
const listOptionIsCreditRequest = (
  item: OrderHistoryListOption,
): item is ListOption<CreditRequestBatchListOption> => {
  return Boolean(item.metadata?.hasOwnProperty('creditRequest'));
};

export type Props = {
  consumptionTax?: ConsumptionTax[] | null;
  displayItemPrices: boolean;
  listItemContainerStyleFn?: (
    option: OrderHistoryListOption,
  ) => StyleProp<ViewStyle>;
  onCreditRequestSelect: (creditRequestUUID: string) => void;
  onInvoiceSelect: (invoiceUUID: string) => void;
  onOrderSelect: (orderUUID: string) => void;
} & Omit<
  RecyclerListProps<OrderHistoryListOption>,
  'estimatedItemSize' | 'renderItem'
>;

export const OrderHistoryList: React.FC<Props> = ({
  consumptionTax,
  displayItemPrices,
  listItemContainerStyleFn,
  onCreditRequestSelect,
  onInvoiceSelect,
  onOrderSelect,
  ...rest
}) => {
  return (
    <RecyclerList
      estimatedItemSize={LIST_ITEM_HEIGHT + LIST_ITEM_MARGIN}
      ItemSeparatorComponent={null}
      keyExtractor={(item) => {
        if (listOptionIsPepperOrder(item)) {
          const { order } = item.metadata!;
          return [
            item.title,
            item.value,
            // mutation to set invoice as seen will update the cache so include it in
            // the key to tell the list to rerender the row
            order.order_invoices[0]?.seen ?? false,
            order.order_tracking?.uuid ?? null,
            order.order_tracking?.order_completed_delivery_datetime ?? null,
            order.order_tracking?.lower_bound_delivery_datetime ?? null,
            order.order_tracking?.upper_bound_delivery_datetime ?? null,
          ].join('-');
        } else if (listOptionIsErpOrder(item)) {
          const { invoice } = item.metadata!;
          return [
            item.title,
            item.value,
            // mutation to set invoice as seen will update the cache so include it in
            // the key to tell the list to rerender the row
            invoice.seen ?? false,
          ].join('-');
        }

        // Fallback for the sake of typing, it should never be reached
        return [item.title, item.value].join('-');
      }}
      renderItem={({ item }) => {
        return (
          <View
            style={[styles.listItemContainer, listItemContainerStyleFn?.(item)]}
          >
            {listOptionIsPepperOrder(item) ? (
              <OrderHistoryListItem
                consumptionTax={consumptionTax}
                displayItemPrices={displayItemPrices}
                onPress={onOrderSelect}
                order={item.metadata!.order}
                orderFees={item.metadata!.orderFees}
              />
            ) : listOptionIsErpOrder(item) ? (
              <ExternalOrderHistoryListItem
                consumptionTax={consumptionTax}
                invoice={item.metadata!.invoice}
                onPress={onInvoiceSelect}
              />
            ) : listOptionIsCreditRequest(item) ? (
              <CreditRequestListItem
                creditRequest={item.metadata!.creditRequest}
                onPress={onCreditRequestSelect}
              />
            ) : null}
          </View>
        );
      }}
      {...rest}
    />
  );
};

const styles = StyleSheet.create({
  listItemContainer: {
    marginHorizontal: LIST_ITEM_MARGIN,
    marginVertical: LIST_ITEM_MARGIN / 2,
  },
});
